/**
 * Friendly empty/error state with optional retry. Used inside ChartCard and
 * wherever a section of the dashboard has no data yet.
 */
import React from 'react';
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import { cn } from '../../../../lib/cn';

export interface EmptyStateProps {
  variant?: 'empty' | 'error' | 'loading';
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'empty',
  title,
  message,
  onRetry,
  className,
}) => {
  const isError = variant === 'error';
  const Icon = isError ? AlertCircle : Inbox;
  const iconColor = isError ? 'text-rose-400' : 'text-gray-400';

  return (
    <div
      className={cn(
        'flex min-h-[160px] flex-col items-center justify-center rounded-md border border-dashed border-gray-200 px-6 py-8 text-center',
        isError ? 'border-rose-200 bg-rose-50/40' : 'bg-gray-50/40',
        className,
      )}
    >
      <Icon className={cn('mb-2 h-8 w-8', iconColor)} aria-hidden />
      {title && (
        <p className="text-sm font-semibold text-gray-800">{title}</p>
      )}
      {message && (
        <p className="mt-1 max-w-sm text-xs text-gray-500">{message}</p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try again
        </button>
      )}
    </div>
  );
};
